import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { translate } from "../../common";
import { FbxLog } from "../../common/utils/logging.utils";

import { useHasRoles, useModulePage, useModules, useQueryParams, useUserPermissions } from "../../hooks";
import { MessageType } from "../../redux/message-box.slice";
import ErrorBoundary from "../error-handling/error-boundary";
import { ProgressDialog } from "../progress-dialog";



export interface AuthenticatedRouteProps {
    roles?: string[];
    children: JSX.Element;
    id?: string;
}



/**
 * Guards a route by enforcing authentication, module permissions, and optional role requirements before rendering children.
 * @param props Route metadata including required roles and the component to render when access is granted.
 * @returns JSX element that either renders the secured children or redirects the user.
 */
export function RequireAuth(props: AuthenticatedRouteProps) {

    /** States:
     *  - unknown: Initial state before validation is complete.
     *  - valid: User is authenticated and has the required permissions.
     *  - notAuthorized: User is authenticated, but does not have permissions for the current page.
     *  - unauthenticated: User is not authenticated and will be redirected to the signin page.
     *  - busy: Validation is in progress.
     */


    const [, allParams] = useQueryParams();
    const [, hasRoles] = useHasRoles();
    const permissions = useUserPermissions();
    const location = useLocation();
    const modulePage = useModulePage();
    const modules = useModules();
    const [_state, setState] = useState<string>("unknown");
    const [_messageInfo, setMessageInfo] = useState<any>(null)

    const hasParams = location.pathname.substring(1) || location.search;
    const requiredRoles = props.roles ?? [];
    const requiredRolesSignature = requiredRoles.join("|");

    const [_lastPage, setLastPage] = useState<string>("")

    const getUrlParams = () => {
        let params: string[] = [];
        let redirect = location.pathname.substring(1);
        if (redirect) {
            params.push(`redirect=${redirect}`);
        }
        let inParams = allParams();
        Object.entries(inParams).forEach(([key, value]) => {
            if (key !== "redirect") {
                params.push(`${key}=${value}`);
            }
        });
        return params.join("&");

    }

    useEffect(() => {
        if (location.pathname !== _lastPage) {
            FbxLog.log("Route changed, resetting validation state", _lastPage, location.pathname);
            setLastPage(location.pathname);
            setState("unknown");
            setMessageInfo(null);
        }
    }, [location.pathname])

    useEffect(() => {
        // Always call the hook, handle the condition inside
        if (modules.length > 0) {
            FbxLog.log("5: Checking module page permissions on dynamic pages for", location.pathname, modules.length);
            let pageInfo = modulePage("dynamic");
            if (pageInfo !== undefined && pageInfo.page?.id) {
                let tokenPermission = permissions[pageInfo?.module?.id ?? ""];
                if (tokenPermission) {
                    let view = tokenPermission.view?.pages?.find(p => p === pageInfo?.page?.id);
                    let edit = tokenPermission.edit?.pages?.find(p => p === pageInfo?.page?.id);
                    let isValid = view !== undefined || edit !== undefined;
                    if (!isValid) {
                        let message = translate("INVALID_RIGHTS_PAGE", { page: pageInfo?.page?.label, module: translate(pageInfo?.module?.label) });
                        setMessageInfo({ message: message, messageType: MessageType.Error });
                    }
                    setState(isValid ? "valid" : "notAuthorized");
                    return;

                }
                else {
                    let message = translate("INVALID_RIGHTS_PAGE", { page: pageInfo?.page?.label, module: translate(pageInfo?.module?.label) });
                    setMessageInfo({ message: message, messageType: MessageType.Error });

                }
            }
            FbxLog.log("6: Checking module page permissions on system pages for", location.pathname, modules.length);
            const systemPageInfo = modulePage("system");
            if (systemPageInfo) {
                let isValid = true;
                if (requiredRoles.length > 0) {
                    isValid = hasRoles(requiredRoles);

                    if (!isValid) {
                        let message = translate("INVALID_RIGHTS_ROLES", { page: translate(systemPageInfo?.page?.label ?? ""), module: translate(systemPageInfo?.module?.label ?? "") });
                        setMessageInfo({ message: message, messageType: MessageType.Error });
                    }
                }
                setState(isValid ? "valid" : "notAuthorized");
                return;
            }

        } else {
            FbxLog.log("6: Checking module page permissions for", location.pathname, modules.length);
        }
    }, [permissions, modules, _lastPage, location.pathname, requiredRolesSignature, hasRoles]);


    FbxLog.log("Auth Validation", _state)
    switch (_state) {
        case "unauthenticated":
            return <Navigate to={hasParams ? `/signin?${getUrlParams()}` : "/signin"} state={{ from: location }} />
        case "unknown":
        case "busy":
            return <ProgressDialog open={true} />;
        case "notAuthorized":
            return <Navigate to={"/"} state={_messageInfo} />
        default:
            return <ErrorBoundary>{props.children}</ErrorBoundary>;
    }


}
